import React from 'react'
import { getAssetHolderQuery } from '../../../../api/asset-holders/assetHolderApi'
import LoadingComponent from '../../../shared/components/ui-widgets/LoadingComponent'
import { Alert, Box, Button, Chip, Divider, Grid, Link, List, ListItem, Typography } from '@mui/material'
import { type AssetHolderUpdateDto } from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import UpdateProfileComponent from './UpdateProfileComponent'
import TwoFaSetupComponent from './TwoFaSetupComponent'
import { useNavigate } from 'react-router-dom'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import ForgotPasswordComponent from '../../../auth/components/ForgotPasswordComponent'
import UpdateEmailAddressComponent from './UpdateEmailAddressComponent'

interface DisplayDetail {
  label: string
  value: string | number
}

const ProfileTabComponent: React.FC = () => {
  const { isLoading: fetchingAssetHolder, data: assetHolderDetails, isSuccess, refetch, isError, error } = getAssetHolderQuery()

  const navigate = useNavigate()

  const getProfileDetails = (assetHolder: AssetHolderUpdateDto): DisplayDetail[] => {
    return [
      { label: 'First Name', value: assetHolder.legacyFirstName },
      { label: 'Last Name', value: assetHolder.legacyLastName },
      { label: 'Date of Birth', value: assetHolder.legacyDob ?? '-' },
      { label: 'Phone number', value: assetHolder.legacyPhoneNumber ?? '-' },
      { label: 'Residential Address', value: assetHolder.legacyAddress ?? '-' },
      { label: 'City', value: assetHolder.legacyCity ?? '-' },
      { label: 'Postal Code / ZIP', value: assetHolder.legacyPostalCode ?? '-' },
      { label: 'State / Province / Region ', value: assetHolder.legacyRegion ?? '-' },
      { label: 'Country', value: assetHolder.legacyCountry ?? '-' },
      { label: 'US Citizen', value: assetHolder.usCitizenStatus.isUsCitizen ? 'Yes' : 'No' }
    ]
  }

  const hasInstitutionRole = (assetHolder: AssetHolderUpdateDto): boolean => {
    return assetHolder.assetHolderRoles.find(x => x.legacyRole === 'INSTITUTION') !== undefined
  }

  const getKycDetails = (assetHolder: AssetHolderUpdateDto): DisplayDetail[] => {
    return [
      { label: 'Status', value: assetHolder.latestLegacyKycResult?.status ?? '-' },
      { label: 'KYC Date', value: assetHolder.latestLegacyKycResult?.kycDateTime ?? '-' }
      // { label: 'Provider', value: assetHolder.latestLegacyKycResult?.provider ?? '-' }
    ]
  }

  const manualReview = (assetHolder: AssetHolderUpdateDto): boolean => {
    return !assetHolder.hasPassedKyc && assetHolder.latestLegacyKycResult?.status === 'MANUAL_REVIEW'
  }

  return (
    <>
      {fetchingAssetHolder && <LoadingComponent/>}
      {isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(error)}/>}

      {isSuccess && assetHolderDetails !== null &&
        <>
          <Box sx={{ width: '100%' }}>
            <Typography variant={'h3'}>Email</Typography>

            <Grid container spacing={2} sx={{ mt: 1 }} >
              <Grid item md={6} xs={12} >
                <Typography variant={'h4'}>Email Address</Typography>
                <Typography variant={'body1'}>{assetHolderDetails.legacyEmailAddress}</Typography>
                {/* <Typography variant={'caption'}>Please <Link href={'https://thesunexchange.com/contact-us'} target={'_blank'} color={'primary'}>contact support</Link> if you wish to change your email address.</Typography> */}
                <UpdateEmailAddressComponent assetHolderDetails={assetHolderDetails} />
              </Grid>
              <Grid item md={3} xs={12}>
                <Typography variant={'h4'}>Email Status</Typography>
                <Typography
                  variant={'body1'}>{assetHolderDetails.registrationState === 'AWAITING_EMAIL_CONFIRMATION' ? 'Unverified' : 'Verified'}</Typography>
              </Grid>
              <Grid item md={3} xs={6}>
                {assetHolderDetails.vipMember &&
                  <Chip label={'VIP Member'} variant={'outlined'} color={'default'} icon={<Brightness7Icon />} />
                }
              </Grid>
              {/*  <Grid item md={6} xs={12}> */}
            {/*    <Alert severity="warning">Please contact support if you wish to change your email address.</Alert></Grid> */}
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }}/>

          <Box sx={{ width: '100%' }}>
            <Typography variant={'h3'}>Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }} >
              <Grid item md={12}>
                <Typography variant={'caption'}>Your details will be shown on your solar cell owner agreements.</Typography>
              </Grid>
              {getProfileDetails(assetHolderDetails).map((detail, index) => (
                <Grid key={index} item md={3} xs={6}>
                  <Typography variant={'h4'}>{detail.label}</Typography>
                  <Typography variant={'body1'}>{detail.value}</Typography>
                </Grid>
              ))}

              <Grid item md={6} xs={6}>
                <Typography variant={'h4'}>Profile Type</Typography>
                <Typography variant={'body1'}>{hasInstitutionRole(assetHolderDetails) ? 'Institution' : 'Individual'}</Typography>
                <Typography variant={'caption'}>Please <Link href={'https://thesunexchange.com/contact-us'} target={'_blank'} color={'primary'}>contact support</Link> if you wish to change your profile type.</Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex' }} justifyContent={'space-between'}>
              <UpdateProfileComponent assetHolderDetails={assetHolderDetails}/>
              <ForgotPasswordComponent title={'Change Password'} />

            </Box>

          </Box>

          <Divider sx={{ my: 3 }}/>

          <Box sx={{ width: '100%' }}>
            <Typography variant={'h3'}>KYC</Typography>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }} >
              {getKycDetails(assetHolderDetails).map((detail, index) => (
                <Grid key={index} item md={3} xs={6}>
                  <Typography variant={'h4'}>{detail.label}</Typography>
                  <Typography variant={'body1'}>{detail.value}</Typography>
                </Grid>
              ))}
              {assetHolderDetails.usCitizenStatus.isUsCitizen &&
                <Grid item md={3} xs={6}>
                  <Chip label={'US Citizen'} color={'info'} variant={'outlined'} />
                </Grid>
              }

              <Grid item md={12}>
                <Typography variant={'caption'}>We&apos;re following international KYC (Know your customer) and AML (Anti-money laundering) guidelines.</Typography>
              </Grid>
            </Grid>

            { !assetHolderDetails.hasPassedKyc && !manualReview(assetHolderDetails) && (
              <>

                <Grid item md={12}>
                  <Alert severity="warning" sx={{ mt: 3, mb: 3 }}>
                    Your KYC has not been completed.
                  </Alert>

                  <Typography variant={'body1'} sx={{ mb: 2 }}>We are required to validate your identity before any funds can be withdrawn.
                    Therefore we have to perform a KYC check in alignment with the South African government’s KYC and AML laws and regulations.</Typography>
                  <Typography variant={'body1'} sx={{ mb: 4 }}>We require ALL of the following documents to be submitted in order to complete the necessary KYC.</Typography>

                  <Typography variant={'h4'}>For Individuals: </Typography>

                  <ul style={{ paddingLeft: '20px', marginTop: 0 }} >
                    <li style={{ marginBottom: '5px' }}>
                      <Typography variant={'body1'}>A clear copy/photo of your ID (front and back) OR Passport</Typography>
                    </li>
                    <li style={{ marginLeft: '40px', marginBottom: '5px' }}>
                      <Typography variant={'body1'}>Please make sure the image is clear and includes a picture of the individual</Typography>
                    </li>
                    <li style={{ marginBottom: '5px' }}>
                      <Typography variant={'body1'}>Proof of residential address (Utility bill, cellphone bil or bank statement)</Typography>
                    </li>
                    <li style={{ marginBottom: '5px' }}>
                      <Typography variant={'body1'}>For ZAR Cashouts: A bank confirmation letter</Typography>
                    </li>
                  </ul>

                  <Typography variant={'body1'} sx={{ mb: 2 }}>
                    Send all required documents to <Link href={'mailto:enquiries@thesunexchange.com'}
                                                         underline={'hover'}
                                                         variant={'body1'} color={'secondary'}
                                                         sx={{}}>enquiries@thesunexchange.com</Link>
                  </Typography>

                  <Typography variant={'h4'}>For Businesses & NPO’s:</Typography>

                  <Typography variant={'body1'} sx={{ mb: 4 }}>
                    Please send an email to <Link href={'mailto:enquiries@thesunexchange.com'}
                                                                              underline={'hover'}
                                                                              variant={'body1'} color={'secondary'}
                                                                              sx={{}}>enquiries@thesunexchange.com</Link> and we will send you a form to complete along with the list of documents required.
                  </Typography>

                  <Typography variant={'body1'} sx={{ mb: 4 }}>
                    Please note that our average turn around time to respond to requests or complete a KYC process is a minimum of three business days.
                  </Typography>

                  <Typography variant={'body1'} sx={{ mb: 2 }}>
                    If you have any questions about the KYC process please contact us at <Link href={'mailto:enquiries@thesunexchange.com'}
                                                                                                                                          underline={'hover'}
                                                                                                                                          variant={'body1'} color={'secondary'}
                                                                                                                                          sx={{}}>enquiries@thesunexchange.com</Link>
                  </Typography>

                </Grid>

              </>

            // <Button
            //   variant={'contained'}
            //   color={'primary'}
            //   onClick={() => { navigate('verify-identity') }}
            // >
            //   Verify Identity
            // </Button>
            )}

            { manualReview(assetHolderDetails) && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                Your application is currently undergoing a manual review and may take a few days. Please keep an eye on your email and dashboard, we&apos;ll keep you updated.
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 3 }}/>

          <Box sx={{ maxWidth: '700px' }}>
            <Typography variant={'h3'}>2FA</Typography>

            <Box sx={{ mt: 2 }}>
              {assetHolderDetails.twoFAStatus &&
                <>
                  <Typography variant={'body1'}>Your account security has <b>leveled up</b> to use two-factor authentication.</Typography>
                  <Typography variant={'body1'}>Your account is ready to verify sensitive actions with an OTP (one-time pin).</Typography>
                </>
              }

              {!assetHolderDetails.twoFAStatus &&
                <>
                  <Typography variant={'body1'}>Your account is already setup to verify sensitive actions with an OTP
                    (one-time pin).</Typography>
                  <Typography variant={'body1'}>When performing the following actions, we will email you an OTP (one-time
                    pin) and prompt you to verify before proceeding:</Typography>
                </>
              }
              <ul style={{ paddingLeft: '20px' }}>
                <li><Typography variant={'body1'}>update your profile details</Typography></li>
                <li><Typography variant={'body1'}>download your activity statement</Typography></li>
                <li><Typography variant={'body1'}>add or update your cashout receiving accounts</Typography></li>
                <li><Typography variant={'body1'}>make a settlement request</Typography></li>
              </ul>
              {!assetHolderDetails.twoFAStatus &&
                <Typography variant={'body1'}>We recommend that you further improve your account security by installing a
                  two-factor authentication app like Authy, Google Authenticator or Microsoft Authenticator on your
                  mobile. We’ll guide you through the setup.</Typography>
              }

              {!assetHolderDetails.twoFAStatus &&
                <Box sx={{ mt: 2 }}>
                  <TwoFaSetupComponent onSuccess={async () => await refetch()} />
                </Box>
              }
            </Box>
          </Box>
        </>
      }
    </>
  )
}

export default ProfileTabComponent
